/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/features/chat/
// WebChatRoom.tsx`. Three-row layout (header / scrolling messages /
// composer) matching Figma file Y95LG1oFzU2PHIvFuMYTfJ → Chat page,
// node 2804:25678.
//
// Presentational only. The host (web Dexie/MLS, desktop AsyncStorage)
// supplies the messages array, send/retry callbacks, and peer
// metadata via `ChatRoomProps`.
//
// Bubble rendering is delegated to the existing `<MessageRow>` from
// `@ping/ui/chat/MessageRow`, which already handles failed/pending
// states + the retry control. This component is responsible for the
// day-grouping headers and the composer.
//
// The composer is the shared `<ChatRoomComposerRich />` from
// `../chat/ChatRoomComposer` — it owns the rich-text toolbar
// (bold/italic/strike/code/codeBlock/lists/quote), the attachment
// menu (photo/doc/poll/location/voice), and the reply-quote chip.
// Poll + Location entries dispatch through the shared
// `ChatRoomShellHandlersProvider` mounted upstream by the host;
// photo/doc/voice entries are hidden unless the host wires the
// corresponding callback.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	findNodeHandle,
	type NativeScrollEvent,
	type NativeSyntheticEvent,
	Pressable,
	ScrollView,
	Text,
	View,
} from "react-native";
import { ChatRoomComposerRich } from "../chat/ChatRoomComposer";
import type { Message } from "../sync/persistence";
import { ChatRoomHeader } from "./ChatRoomHeader";
import { PinnedBar } from "./PinnedBar";
import { TypingIndicator } from "./TypingIndicator";
import type {
	ChatMessageVariant,
	ChatRoomProps,
} from "./ChatRoom.types";
import { MessageRow } from "./MessageRow";
import { ComposerScrollDownFab } from "./composer/ComposerScrollDownFab";
import { AttachmentTray } from "./composer/AttachmentTray";
import { useUiPreferencesStore } from "../preferences/uiPreferencesStore";

// Initials helper for stacked-mode avatars resolved from a display name.
function initialsFromName(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return (parts[0]!.charAt(0) || "?").toUpperCase();
	return (
		(parts[0]!.charAt(0) || "") + (parts[parts.length - 1]!.charAt(0) || "")
	).toUpperCase();
}

// Room events (event / call) are room-owned timeline rows. They stand alone,
// break adjacent sender runs, and receive no avatar / name / bubble-tail chrome.
function isRoomEvent(v: ChatMessageVariant | undefined): boolean {
	return v?.kind === "event" || v?.kind === "call";
}

// Distance (px) from the bottom beyond which the scroll-to-latest FAB
// (Figma 2804:26738) appears.
const SCROLL_FAB_THRESHOLD = 240;

// Render-window cap. We slice the messages array to the most-recent N
// before grouping + rendering so a 5,000-message conversation costs
// O(200) DOM nodes instead of O(5,000) per re-render. The first
// "Load earlier messages" tap doubles the window; subsequent taps
// keep doubling until everything is on-screen. This is a focused
// performance win that doesn't require swapping to FlatList — the
// scroll-position semantics, day-divider grouping, and hover-driven
// actions all keep working unchanged.
const DEFAULT_RENDER_WINDOW = 200;

export function ChatRoom(props: ChatRoomProps): JSX.Element {
	const {
		conversationId,
		layoutMode,
		selfName,
		selfAvatarUri,
		peerName,
		peerAvatarUri,
		peerPresence,
		peerSubtitle,
		activeCall,
		gameSessions,
		onGameMove,
		messages,
		isLoading,
		error,
		inviteWarning,
		sendError,
		canSend,
		onSend,
		onRetry,
		onReplyPress,
		onMentionPress,
		onAvatarPress,
		replyTo,
		replyToThumbnailUri,
		replyToThumbnailKind,
		onCancelReply,
		editingMessage,
		onSubmitEdit,
		onCancelEdit,
		scrollToMessageRef,
		onComposerTextChange,
		onScheduleMessage,
		scheduledMessages,
		onCancelScheduled,
		typingLabel,
		composerDisabled,
		composerReadOnly,
		composerReadOnlyLabel,
		onPickImages,
		onPickDoc,
		onCaptureCamera,
		stagedAttachments,
		onRemoveStagedAttachment,
		onPressStagedAttachment,
		onPasteImage,
		onStartVoice,
		onSendVoiceMessage,
		onCancelVoiceMessage,
		subscribeAmplitudes,
		gifProvider,
		onSendGif,
		stickerProvider,
		onSendSticker,
		pressEnterToSend,
		mentionCandidates,
		onAudioCall,
		onVideoCall,
		onSearch,
		onMore,
		onOpenChatSettings,
		onAudioCallMenu,
		messageVariants,
		isGroup,
		senderNames,
		senderAvatars,
		threadAffordances,
		pinnedMessageIds,
		savedMessageIds,
		starredMessageIds,
		pinnedMessages,
		onJumpToPinned,
		onUnpinMessage,
		onReactPress,
		onReplyPressFromHover,
		onEditPressFromHover,
		onMorePress,
		reactionsByMessageId,
		onToggleReaction,
		editedAtByMessageId,
		richBodyByMessageId,
		onMenuAction,
		resolveReaderName,
		resolveReaderAvatar,
		groupNonSelfMemberCount,
	} = props;

	// Pre-build a Set for O(1) pinned-id lookups.
	const pinnedSet = useMemo(
		() => new Set(pinnedMessageIds ?? []),
		[pinnedMessageIds],
	);
	// Same for saved / starred markers (drive Unsave / Unstar menu labels).
	const savedSet = useMemo(
		() => new Set(savedMessageIds ?? []),
		[savedMessageIds],
	);
	const starredSet = useMemo(
		() => new Set(starredMessageIds ?? []),
		[starredMessageIds],
	);

	// Normalize variants to a Map-like getter so hosts can pass either
	// a `Map` or a plain object. Lookup is O(1) either way.
	const lookupVariant = useCallback(
		(id: string): ChatMessageVariant | undefined => {
			if (messageVariants === undefined) return undefined;
			if (messageVariants instanceof Map) return messageVariants.get(id);
			return (messageVariants as Record<string, ChatMessageVariant>)[id];
		},
		[messageVariants],
	);

	const scrollRef = useRef<ScrollView>(null);
	const lastCountRef = useRef(0);

	// Latest-value refs so the row callbacks below can be `[]`-stable. `messages`
	// gets a brand-new array reference on every send/receive (useMessages re-sorts
	// into a fresh array per emit), so a callback that closed over `messages`
	// would re-identify on every message and — because it's handed to EVERY
	// MessageRow — defeat the row's React.memo, re-rendering the whole visible
	// window per message. Reading the array off a ref keeps the callbacks stable.
	const messagesRef = useRef(messages);
	messagesRef.current = messages;
	const onRetryRef = useRef(onRetry);
	onRetryRef.current = onRetry;

	// Per-message row nodes + a transient highlight, so tapping a reply
	// quote jumps to (and briefly flashes) the referenced message.
	const messageNodesRef = useRef<Map<string, View>>(new Map());
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const scrollToMessage = useCallback((target: string): void => {
		// `target` may be a local row id OR a content hash (reply quotes
		// reference the replied message by its content hash). Resolve to the
		// rendered row's local id either way. Read the array off the ref so this
		// callback stays `[]`-stable and doesn't re-identify per message.
		const messages = messagesRef.current;
		const match = messages.find(
			(mm) => mm.id === target || mm.contentHash === target,
		);
		const messageId = match?.id ?? target;
		const node = messageNodesRef.current.get(messageId);
		if (node === undefined) return;
		// On web the View ref forwards to the DOM element → use native
		// smooth scroll-into-view. On native fall back to measureLayout
		// against the ScrollView and `scrollTo`.
		const dom = node as unknown as {
			scrollIntoView?: (opts?: ScrollIntoViewOptions) => void;
		};
		if (typeof dom.scrollIntoView === 'function') {
			dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} else {
			const scroll = scrollRef.current;
			const scrollHandle = scroll !== null ? findNodeHandle(scroll) : null;
			if (scroll !== null && scrollHandle !== null) {
				node.measureLayout(
					scrollHandle,
					(_x, y) => scroll.scrollTo({ y: Math.max(0, y - 16), animated: true }),
					() => undefined,
				);
			}
		}
		setHighlightedId(messageId);
		if (highlightTimerRef.current !== null) clearTimeout(highlightTimerRef.current);
		highlightTimerRef.current = setTimeout(() => {
			setHighlightedId((cur) => (cur === messageId ? null : cur));
		}, 1600);
	}, []);

	// Hand the imperative scroll-to-message up to the host (if it passed a
	// ref) so external surfaces — e.g. the media viewer's "Go to message"
	// — can jump to a row. Cleared on unmount.
	useEffect(() => {
		if (scrollToMessageRef === undefined) return undefined;
		scrollToMessageRef.current = scrollToMessage;
		return () => {
			scrollToMessageRef.current = null;
		};
	}, [scrollToMessageRef, scrollToMessage]);

	// Clear any pending highlight timer on unmount so it can't fire
	// `setHighlightedId` after the component is gone (no-op setState warning /
	// leak). The timer is (re)armed synchronously in `scrollToMessage`.
	useEffect(
		() => () => {
			if (highlightTimerRef.current !== null) {
				clearTimeout(highlightTimerRef.current);
			}
		},
		[],
	);

	// Resolve the active layout density: explicit prop wins, otherwise
	// the persisted UI preference (Settings → Appearance toggle).
	const storeLayout = useUiPreferencesStore((s) => s.messageLayout);
	const isStacked = (layoutMode ?? storeLayout) === "stacked";

	// ── Stable props for the React.memo'd MessageRow ─────────────────────────
	// MessageRow is memoized; feeding it freshly-allocated avatar objects or a
	// fresh per-row reaction closure on every render defeats the memo and
	// re-renders every row. Stabilize both here.
	type RowAvatar = { readonly initials: string; readonly imageUri?: string };
	// The peer's avatar (1:1) and self avatar (stacked) are one shared object for
	// every row that uses them, recomputed only when the id/uri behind them change.
	const peerAvatar = useMemo<RowAvatar>(
		() => ({
			initials: initialsFromName(peerName),
			...(peerAvatarUri !== undefined ? { imageUri: peerAvatarUri } : {}),
		}),
		[peerName, peerAvatarUri],
	);
	const selfAvatar = useMemo<RowAvatar>(
		() => ({
			initials: initialsFromName(selfName ?? "You"),
			...(selfAvatarUri !== undefined ? { imageUri: selfAvatarUri } : {}),
		}),
		[selfName, selfAvatarUri],
	);
	// Per-sender group avatar WITH the initials fallback (bubble mode's last-of-run
	// avatar gutter must never be empty). Cached per senderId so repeated rows from
	// the same sender share one reference; the cache resets when the host's maps do.
	const resolveGroupBubbleAvatar = useMemo(() => {
		const cache = new Map<string, RowAvatar>();
		return (senderId: string): RowAvatar => {
			let a = cache.get(senderId);
			if (a === undefined) {
				a =
					senderAvatars?.[senderId] ??
					({ initials: initialsFromName(senderNames?.[senderId] ?? "?") } as RowAvatar);
				cache.set(senderId, a);
			}
			return a;
		};
	}, [senderAvatars, senderNames]);

	// One stable reaction handler per message id: the per-row closure
	// `(emoji) => onToggleReaction(id, emoji)` is cached so MessageRow's memo isn't
	// broken, while always dispatching to the latest `onToggleReaction` via a ref.
	const onToggleReactionRef = useRef(onToggleReaction);
	onToggleReactionRef.current = onToggleReaction;
	const rowToggleHandlers = useRef<Map<string, (emoji: string) => void>>(
		new Map(),
	);
	const getToggleReactionHandler = useCallback(
		(messageId: string): ((emoji: string) => void) => {
			let h = rowToggleHandlers.current.get(messageId);
			if (h === undefined) {
				h = (emoji: string) => onToggleReactionRef.current?.(messageId, emoji);
				rowToggleHandlers.current.set(messageId, h);
			}
			return h;
		},
		[],
	);

	// One stable ref-callback per message id. An inline `ref={(node) => …}` on
	// each row is a fresh closure every render, so React detaches (null) and
	// reattaches every visible row's node ref on every ChatRoom render — pure
	// churn. Caching the callback per id keeps the ref identity stable so React
	// only (de)registers a node when the row actually mounts/unmounts. Cleared on
	// chat switch alongside `rowToggleHandlers` so it can't grow unbounded.
	const rowNodeRefHandlers = useRef<Map<string, (node: View | null) => void>>(
		new Map(),
	);
	const getMessageNodeRef = useCallback(
		(messageId: string): ((node: View | null) => void) => {
			let h = rowNodeRefHandlers.current.get(messageId);
			if (h === undefined) {
				h = (node: View | null) => {
					if (node) messageNodesRef.current.set(messageId, node);
					else messageNodesRef.current.delete(messageId);
				};
				rowNodeRefHandlers.current.set(messageId, h);
			}
			return h;
		},
		[],
	);

	// Scroll-to-latest FAB (Figma 2804:26738) — shown when the user has
	// scrolled away from the bottom of the message list.
	const [showScrollFab, setShowScrollFab] = useState(false);
	const handleScroll = useCallback(
		(e: NativeSyntheticEvent<NativeScrollEvent>): void => {
			const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
			const distanceFromBottom =
				contentSize.height - (contentOffset.y + layoutMeasurement.height);
			setShowScrollFab(distanceFromBottom > SCROLL_FAB_THRESHOLD);
		},
		[],
	);

	// Auto-scroll to the bottom whenever the message count changes
	// (new bubble lands, conversation switches). The previous web
	// implementation imperatively wrote `scrollTop = scrollHeight`;
	// the RN equivalent calls `scrollToEnd({ animated: false })`,
	// which works on react-native-web and on native macOS.
	useEffect(() => {
		if (messages.length !== lastCountRef.current) {
			lastCountRef.current = messages.length;
			// Defer to next tick so RN has measured the new rows
			// before we scroll.
			requestAnimationFrame(() => {
				scrollRef.current?.scrollToEnd({ animated: false });
			});
		}
	}, [messages.length]);

	// Reset scroll baseline when the user picks a different
	// conversation. The composer's internal draft / format / menu
	// state is reset by keying the composer on `conversationId` below.
	useEffect(() => {
		lastCountRef.current = 0;
		// Drop the per-message handler caches on chat switch. Without this they
		// retain one closure for EVERY message id ever rendered across the whole
		// session (only ever added to, never pruned) — an unbounded leak that
		// grows the longer the app stays open. Both are recreated lazily on the
		// next render, so clearing here is free. (`messageNodesRef` is NOT cleared
		// here: it self-prunes via each row's unmount `ref(null)` callback, and
		// clearing it in this post-commit effect would wipe the just-mounted rows
		// of the conversation we're switching TO.)
		rowToggleHandlers.current.clear();
		rowNodeRefHandlers.current.clear();
	}, [conversationId]);

	// Adapter from `ChatRoomComposerRich`'s `(text, inReplyTo?)` shape
	// to the host's `(text, retryRowId?)` contract. `inReplyTo` is a
	// parent message id used for reply quoting — the host's retry
	// pipeline is independent (it routes through `MessageRow`'s retry,
	// not the composer). We intentionally drop `inReplyTo` here: the
	// MLS envelope plumbing for reply parents lands in a follow-up.
	// The reply chip itself still renders.
	const handleComposerSend = useCallback(
		async (text: string, _inReplyTo?: string): Promise<void> => {
			const trimmed = text.trim();
			// Allow attachment-only sends: when the user has staged
			// images/files, an empty text box is a valid composite send
			// (the host's onSend / handleSendComposite flushes the staged
			// tray). Previously this early-return swallowed those sends so
			// clicking Send with attachments did nothing.
			const hasStaged =
				stagedAttachments !== undefined && stagedAttachments.length > 0;
			if (trimmed.length === 0 && !hasStaged) return;
			await onSend(trimmed);
		},
		[onSend, stagedAttachments],
	);

	const handleRetryPress = useCallback((messageId: string): void => {
		// Ref-read for `[]` stability — see `messagesRef` note above.
		const failed = messagesRef.current.find((m) => m.id === messageId);
		if (failed !== undefined) {
			onRetryRef.current(failed);
		}
	}, []);

	// Render-window state. Reset to default when the user switches
	// chats so we don't carry over an expanded window from a previous
	// conversation (potential memory hit on cross-chat navigation).
	const [renderWindow, setRenderWindow] = useState<number>(DEFAULT_RENDER_WINDOW);
	useEffect(() => {
		setRenderWindow(DEFAULT_RENDER_WINDOW);
	}, [conversationId]);

	const hasMoreAbove = messages.length > renderWindow;
	const hiddenCount = hasMoreAbove ? messages.length - renderWindow : 0;
	const visibleMessages = useMemo(
		() => (hasMoreAbove ? messages.slice(-renderWindow) : messages),
		[messages, renderWindow, hasMoreAbove],
	);

	const grouped = useMemo(() => groupByDay(visibleMessages), [visibleMessages]);

	return (
		<View
			// Chat-room background: `#11131A` is the dark-mode surface
			// pulled from the Figma composer container (`2804:26702` —
			// extracted RGB 0.067, 0.075, 0.094 ≈ `#111318`; the
			// existing baseline uses `#11131A` which is within 1pt of
			// that — preserved for parity with prior screenshots).
			className="flex-1 min-h-0 flex flex-col bg-light-surface-primary dark:bg-[#11131A]"
		>
			<ChatRoomHeader
				peerName={peerName}
				{...(peerAvatarUri !== undefined ? { peerAvatarUri } : {})}
				{...(peerPresence !== undefined ? { peerPresence } : {})}
				{...(peerSubtitle !== undefined ? { peerSubtitle } : {})}
				{...(activeCall !== undefined ? { activeCall } : {})}
				{...(onAudioCall !== undefined ? { onAudioCall } : {})}
				{...(onVideoCall !== undefined ? { onVideoCall } : {})}
				callsDisabled={composerReadOnly === true}
				{...(onSearch !== undefined ? { onSearch } : {})}
				{...(onMore !== undefined ? { onMore } : {})}
				{...(onAudioCallMenu !== undefined ? { onAudioCallMenu } : {})}
				{...(onOpenChatSettings !== undefined ? { onOpenChatSettings } : {})}
			/>

			{/* In-chat collapsible pinned bar — Figma 3466:7138. Renders
			    directly under the header, above the message list, only
			    when the host supplies at least one pinned message. */}
			{pinnedMessages !== undefined && pinnedMessages.length > 0 ? (
				<PinnedBar
					pinnedMessages={pinnedMessages}
					{...(onJumpToPinned !== undefined ? { onJumpToPinned } : {})}
					{...(onUnpinMessage !== undefined ? { onUnpinMessage } : {})}
				/>
			) : null}

			<ScrollView
				ref={scrollRef}
				accessibilityLiveRegion="polite"
				className="flex-1 min-h-0"
				// Stable hook for the web "Chat background" appearance setting
				// (Settings → Appearance). RN-web maps `dataSet` → a `data-*`
				// attribute the `chat-background.css` rules target under
				// `html[data-chat-bg]`; ignored on RN-macos. Plain leaves the
				// parent surface showing (no rule), so this is inert by default.
				// `dataSet` isn't in the RN ScrollView types, so spread it via
				// an untyped object (the established web-prop pattern here).
				{...({ dataSet: { pingChatThread: "" } } as Record<string, unknown>)}
				onScroll={handleScroll}
				scrollEventThrottle={16}
				// 24px horizontal gutter mirrors the composer's
				// `pad L24/R24` (`2804:26702`); vertical 12 keeps the
				// bubble flush with the day-divider rhythm. Stacked mode
				// drops the horizontal gutter — rows own their 12px inset.
				contentContainerClassName={isStacked ? "py-md" : "px-xl py-md"}
			>
				{isLoading ? (
					<ListSkeleton />
				) : error !== null ? (
					<View className="py-md items-center">
						<Text className="font-sans text-footnote text-light-accent-rose">
							Couldn't load messages.
						</Text>
					</View>
				) : messages.length === 0 ? (
					<EmptyMessages peerName={peerName} />
				) : (
					<View className="flex flex-col gap-xs">
						{hasMoreAbove ? (
							<Pressable
								accessibilityRole="button"
								accessibilityLabel={`Load ${hiddenCount} earlier messages`}
								onPress={() =>
									setRenderWindow((prev) => prev + DEFAULT_RENDER_WINDOW)
								}
								className="self-center my-md px-md py-xs rounded-md bg-light-surface-secondary dark:bg-white/[0.06]"
							>
								<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/70">
									{`Load ${hiddenCount} earlier message${hiddenCount === 1 ? '' : 's'}`}
								</Text>
							</Pressable>
						) : null}
						{grouped.map((group) => (
							<View key={group.day}>
								{/*
									Day separator. Figma `3412:5373`-style
									chip: 32-w base / 14-h / r=8, but for a
									date label (instead of "NEW") we let the
									pill grow to fit. The hairlines on either
									side use `border-secondary` in light /
									`white/8` in dark.
								*/}
								<View className="flex flex-row items-center gap-md my-md">
									<View className="flex-1 h-px bg-light-border-secondary dark:bg-white/[0.08]" />
									<View className="px-sm py-[2px] rounded-md bg-light-surface-secondary dark:bg-white/[0.04]">
										<Text className="font-sans text-[11px] font-semibold leading-[14px] text-light-text-secondary dark:text-white/55 uppercase tracking-wider">
											{group.label}
										</Text>
									</View>
									<View className="flex-1 h-px bg-light-border-secondary dark:bg-white/[0.08]" />
								</View>
								<View className={isStacked ? "flex flex-col" : "flex flex-col gap-[4px]"}>
									{group.messages.map((m, idx) => {
										const variant = lookupVariant(m.id);
										// Room events (event / call) are timeline rows owned by the
										// room, not by any sender. They always stand alone.
										const currentIsEvent = isRoomEvent(variant);
										// Group sender header (Figma 3412:5207):
										// only show when the host marked the
										// conversation as a group AND the
										// bubble is received AND either this
										// is the first message of the group
										// OR the previous message was from a
										// different sender. This collapses
										// consecutive bubbles from one author.
										const prev = group.messages[idx - 1];
										const next = group.messages[idx + 1];
										// Run detection — a "run" is a maximal span of
										// consecutive messages from one author.
										// First-of-run draws the sender name (bubble) or
										// avatar+header (stacked); last-of-run gets the
										// bubble tail and (in group bubbles) the avatar.
										// Room events always break runs on both sides so
										// neither the event nor its neighbours get grouped.
										const prevVariant = prev !== undefined ? lookupVariant(prev.id) : undefined;
										const nextVariant = next !== undefined ? lookupVariant(next.id) : undefined;
										const sameAsPrev =
											!currentIsEvent &&
											!isRoomEvent(prevVariant) &&
											prev !== undefined &&
											prev.isSelf === m.isSelf &&
											prev.senderId === m.senderId;
										const sameAsNext =
											!currentIsEvent &&
											!isRoomEvent(nextVariant) &&
											next !== undefined &&
											next.isSelf === m.isSelf &&
											next.senderId === m.senderId;
										const isFirstOfRun = !sameAsPrev;
										const isLastOfRun = !sameAsNext;
										// Bubble-mode group chrome (Figma 3412:5207):
										// sender name inside the FIRST received bubble of a
										// run; the single avatar anchored to the LAST
										// received bubble; empty gutter on the others.
										// Room events receive none of this chrome.
										const showSender =
											!currentIsEvent && isGroup === true && !m.isSelf && isFirstOfRun;
										const bubbleSenderName =
											showSender && senderNames !== undefined
												? senderNames[m.senderId]
												: undefined;
										// Received bubbles show the sender's avatar at the
										// bottom of the LAST bubble of a run (Figma) — for
										// BOTH group chats (per-sender avatar) and 1:1
										// (the peer's avatar). Earlier bubbles in the run
										// reserve the gutter so the column stays aligned.
										// Room events never render this chrome.
										const bubbleSenderAvatar =
											!currentIsEvent && !m.isSelf && isLastOfRun
												? isGroup === true
													? // Group: prefer the resolved per-sender avatar,
														// but ALWAYS fall back to initials so the last
														// grouped bubble never renders an empty gutter
														// when the avatar hasn't resolved yet.
														resolveGroupBubbleAvatar(m.senderId)
													: peerAvatar
												: undefined;
										const groupAvatarGutter =
											!currentIsEvent && !m.isSelf && bubbleSenderAvatar === undefined;
										// Stacked-mode sender resolution — every
										// first-of-run message gets an avatar + name
										// header, including self (Figma 2804:25812).
										// Room events are sender-agnostic: skip this block.
										let stackedName: string | undefined;
										let stackedAvatar:
											| { readonly initials: string; readonly imageUri?: string }
											| undefined;
										if (isStacked && !currentIsEvent) {
											if (m.isSelf) {
												stackedName = selfName ?? "You";
												stackedAvatar = selfAvatar;
											} else if (isGroup === true) {
												stackedName = senderNames?.[m.senderId];
												stackedAvatar = senderAvatars?.[m.senderId];
											} else {
												stackedName = peerName;
												stackedAvatar = peerAvatar;
											}
										}
										const rowSenderName = isStacked ? stackedName : bubbleSenderName;
										const rowSenderAvatar = isStacked ? stackedAvatar : bubbleSenderAvatar;
										const threadAffordance =
											threadAffordances !== undefined
												? threadAffordances[m.id]
												: undefined;
										const isPinned = pinnedSet.has(m.id);
										const isSaved = savedSet.has(m.id);
										const isStarred = starredSet.has(m.id);
										const reactions =
											reactionsByMessageId !== undefined
												? reactionsByMessageId[m.id]
												: undefined;
										// Prefer the host-supplied map, else fall back to the
										// message's own `editedAt` (surfaced through the adapter
										// from the `com.ping.edit` receive path) so the "(edited)"
										// indicator works without the host building a separate map.
										const editedAt =
											editedAtByMessageId?.[m.id] ?? m.editedAt;
										const handleToggleReaction =
											onToggleReaction !== undefined
												? getToggleReactionHandler(m.id)
												: undefined;
										const richBody =
											richBodyByMessageId !== undefined
												? richBodyByMessageId[m.id]
												: undefined;
										const gameSession =
											variant?.kind === "game" && gameSessions !== undefined
												? gameSessions.get(variant.sessionId)
												: undefined;
										return (
											<View
												key={m.id}
												ref={getMessageNodeRef(m.id)}
												style={
													highlightedId === m.id
														? {
																borderRadius: 12,
																backgroundColor: "rgba(83,126,255,0.12)",
															}
														: undefined
												}
											>
											<MessageRow
												message={m}
												onReplyQuotePress={scrollToMessage}
												{...(variant !== undefined
													? { variant }
													: {})}
												{...(onReplyPress !== undefined
													? { onReplyPress }
													: {})}
												{...(onMentionPress !== undefined
													? { onMentionPress }
													: {})}
												{...(onAvatarPress !== undefined
													? { onAvatarPress }
													: {})}
												onRetryPress={handleRetryPress}
												{...(isStacked
													? { layoutMode: "stacked" as const, showHeader: isFirstOfRun }
													: { isLastOfGroup: isLastOfRun, isFirstOfGroup: isFirstOfRun })}
												{...(rowSenderName !== undefined
													? { senderName: rowSenderName }
													: {})}
												{...(rowSenderAvatar !== undefined
													? { senderAvatar: rowSenderAvatar }
													: {})}
												{...(!isStacked && groupAvatarGutter
													? { groupAvatarGutter: true }
													: {})}
												{...(threadAffordance !== undefined
													? { threadAffordance }
													: {})}
												{...(isPinned ? { isPinned } : {})}
												{...(isSaved ? { isSaved } : {})}
												{...(isStarred ? { isStarred } : {})}
												{...(onReactPress !== undefined
													? { onReactPress }
													: {})}
												{...(onReplyPressFromHover !== undefined
													? { onReplyPressFromHover }
													: {})}
												{...(onEditPressFromHover !== undefined
													? { onEditPressFromHover }
													: {})}
												{...(onMorePress !== undefined
													? { onMorePress }
													: {})}
												{...(reactions !== undefined
													? { reactions }
													: {})}
												{...(handleToggleReaction !== undefined
													? { onToggleReaction: handleToggleReaction }
													: {})}
												{...(editedAt !== undefined
													? { editedAt }
													: {})}
												{...(m.forwarded === true
													? { forwarded: true as const }
													: {})}
												{...(richBody !== undefined
													? { richBody }
													: {})}
												{...(onMenuAction !== undefined
													? { onMenuAction }
													: {})}
												{...(gameSession !== undefined
													? { gameSession }
													: {})}
												{...(onGameMove !== undefined
													? { onGameMove }
													: {})}
												{...(peerName.length > 0
													? { gameOpponentName: peerName }
													: {})}
												{...(resolveReaderName !== undefined
													? { resolveReaderName }
													: {})}
												{...(resolveReaderAvatar !== undefined
													? { resolveReaderAvatar }
													: {})}
												{...(groupNonSelfMemberCount !== undefined
													? { groupNonSelfMemberCount }
													: {})}
											/>
											</View>
										);
									})}
								</View>
							</View>
						))}
					</View>
				)}
			</ScrollView>

			{/* In-list typing indicator — Figma 2918:10454. Animated dots
			    + "X is typing…" pinned just above the composer. */}
			{typingLabel !== undefined && typingLabel !== null && typingLabel.length > 0 ? (
				<View className="px-md">
					<TypingIndicator participants={[]} label={typingLabel} />
				</View>
			) : null}

			{/* Scroll-to-latest FAB — Figma 2804:26738. Floats above the
			    composer, right-aligned, when scrolled away from bottom. */}
			{showScrollFab ? (
				<View className="absolute right-6 bottom-[84px] z-20">
					<ComposerScrollDownFab
						onPress={() =>
							scrollRef.current?.scrollToEnd({ animated: true })
						}
					/>
				</View>
			) : null}

			{inviteWarning !== null && inviteWarning !== undefined && (
				<Text
					accessibilityRole="alert"
					className="px-lg pb-xs font-sans text-caption text-light-accent-amber dark:text-[#d4a017]"
				>
					{inviteWarning}
				</Text>
			)}

			{sendError !== null && sendError !== undefined && (
				<Text
					accessibilityRole="alert"
					className="px-lg pb-xs font-sans text-caption text-light-accent-rose"
				>
					{sendError}
				</Text>
			)}

			{/* Staged-attachment tray — Design-system 4058:6355. Renders
			    above the composer when the host has staged images/files. */}
			{stagedAttachments !== undefined && stagedAttachments.length > 0 ? (
				<AttachmentTray
					attachments={stagedAttachments}
					onRemove={onRemoveStagedAttachment ?? (() => undefined)}
					{...(onPressStagedAttachment !== undefined
						? { onPressStaged: onPressStagedAttachment }
						: {})}
				/>
			) : null}

			{/*
				Shared rich composer. Keyed by `conversationId` so its
				internal draft + format + attach-menu state resets when
				the user switches conversations (matching the previous
				v2 behaviour). On `react-native-web` (apps/web) TipTap
				is not bundled — the composer falls back to the basic
				`<RichTextEditor />` with a plain TextInput and the
				`FormatOptions` toggle row. On `react-native-macos`
				(apps/desktop) the same fallback path runs natively.
			*/}
			{/* When the local user has LEFT the group the ENTIRE composer is
			    locked: dimmed (opacity) AND non-interactive (pointer events off),
			    with a short notice so it's clear why nothing can be typed. */}
			{composerReadOnly === true ? (
				<View className="px-xl pt-sm pb-lg items-center" pointerEvents="none">
					<Text className="text-[13px] text-center text-light-text-tertiary dark:text-dark-text-tertiary">
						{composerReadOnlyLabel ??
							"You can't send messages because you left this group."}
					</Text>
				</View>
			) : (
			<ChatRoomComposerRich
				key={conversationId}
				draftKey={conversationId}
				onSend={handleComposerSend}
				disabled={!canSend || composerDisabled === true}
				hasStagedAttachments={
					stagedAttachments !== undefined && stagedAttachments.length > 0
				}
				{...(replyTo !== undefined ? { replyTo } : {})}
				{...(replyTo !== undefined && replyTo !== null
					? {
							replyToSenderName: replyTo.isSelf
								? (selfName ?? "You")
								: isGroup === true
									? (senderNames?.[replyTo.senderId] ?? peerName)
									: peerName,
							replyToAvatar: replyTo.isSelf
								? {
										initials: initialsFromName(selfName ?? "You"),
										...(selfAvatarUri !== undefined
											? { imageUri: selfAvatarUri }
											: {}),
									}
								: isGroup === true
									? (senderAvatars?.[replyTo.senderId] ?? {
											initials: initialsFromName(peerName),
										})
									: {
											initials: initialsFromName(peerName),
											...(peerAvatarUri !== undefined
												? { imageUri: peerAvatarUri }
												: {}),
										},
						}
					: {})}
				{...(replyToThumbnailUri !== undefined ? { replyToThumbnailUri } : {})}
				{...(replyToThumbnailKind !== undefined ? { replyToThumbnailKind } : {})}
				{...(onCancelReply !== undefined ? { onCancelReply } : {})}
				{...(editingMessage !== undefined ? { editingMessage } : {})}
				{...(onSubmitEdit !== undefined ? { onSubmitEdit } : {})}
				{...(onCancelEdit !== undefined ? { onCancelEdit } : {})}
				{...(onPickImages !== undefined ? { onPickImages } : {})}
				{...(onPickDoc !== undefined ? { onPickDoc } : {})}
				{...(onCaptureCamera !== undefined ? { onCaptureCamera } : {})}
				{...(onStartVoice !== undefined ? { onStartVoice } : {})}
				{...(onSendVoiceMessage !== undefined ? { onSendVoiceMessage } : {})}
				{...(onCancelVoiceMessage !== undefined ? { onCancelVoiceMessage } : {})}
				{...(subscribeAmplitudes !== undefined ? { subscribeAmplitudes } : {})}
				{...(gifProvider !== undefined ? { gifProvider } : {})}
				{...(onSendGif !== undefined ? { onSendGif } : {})}
				{...(stickerProvider !== undefined ? { stickerProvider } : {})}
				{...(onSendSticker !== undefined ? { onSendSticker } : {})}
				{...(pressEnterToSend !== undefined ? { pressEnterToSend } : {})}
				{...(mentionCandidates !== undefined ? { mentionCandidates } : {})}
				{...(onPasteImage !== undefined ? { onPasteImage } : {})}
				{...(onComposerTextChange !== undefined ? { onComposerTextChange } : {})}
				{...(onScheduleMessage !== undefined ? { onScheduleMessage } : {})}
				{...(scheduledMessages !== undefined ? { scheduledMessages } : {})}
				{...(onCancelScheduled !== undefined ? { onCancelScheduled } : {})}
			/>
			)}
		</View>
	);
}

function ListSkeleton(): JSX.Element {
	return (
		<View className="flex flex-col gap-md">
			{Array.from({ length: 5 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton index is fine
					key={i}
					className={`flex flex-row ${i % 2 ? "justify-end" : "justify-start"}`}
				>
					<View
						className={`
							h-10 rounded-[18px] bg-light-surface-secondary dark:bg-white/[0.06]
							${i % 2 ? "w-1/3" : "w-1/2"}
						`}
					/>
				</View>
			))}
		</View>
	);
}

interface EmptyMessagesProps {
	readonly peerName: string;
}

function EmptyMessages({ peerName }: EmptyMessagesProps): JSX.Element {
	const hasName = peerName.length > 0 && peerName !== "New chat";
	return (
		<View className="flex-1 flex flex-col items-center justify-center gap-sm py-xxl">
			<Text className="font-sans text-headline font-semibold leading-[22px] text-light-text-primary dark:text-white text-center">
				{hasName ? `Start the conversation with ${peerName}` : "Say hi"}
			</Text>
			<Text className="font-sans text-footnote leading-[20px] text-light-text-secondary dark:text-white/55 max-w-[320px] text-center">
				Messages here are end-to-end encrypted. Only you and the
				recipient can read them.
			</Text>
		</View>
	);
}

interface MessageGroup {
	readonly day: string;
	readonly label: string;
	readonly messages: Message[];
}

function groupByDay(messages: readonly Message[]): MessageGroup[] {
	const groups = new Map<string, MessageGroup>();
	for (const m of messages) {
		const date = new Date(m.timestamp);
		const day = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
		const existing = groups.get(day);
		if (existing !== undefined) {
			existing.messages.push(m);
		} else {
			groups.set(day, {
				day,
				label: formatDayLabel(date),
				messages: [m],
			});
		}
	}
	return Array.from(groups.values());
}

function formatDayLabel(date: Date): string {
	const now = new Date();
	const yMd = (d: Date): string =>
		`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
	const today = yMd(now);
	const yesterday = yMd(new Date(now.getTime() - 24 * 60 * 60 * 1000));
	const target = yMd(date);
	if (target === today) return "Today";
	if (target === yesterday) return "Yesterday";
	return date.toLocaleDateString(undefined, {
		month: "long",
		day: "numeric",
		year: now.getFullYear() === date.getFullYear() ? undefined : "numeric",
	});
}
